/**
 * City data loading, plus the live re-check against issuing bodies.
 *
 * The shipped GeoJSON is a snapshot. Certificates lapse and get revoked between
 * builds, so after a city loads we ask the certifier directly — from the user's
 * own browser — whether each certificate is still good. The snapshot is the
 * offline fallback; the certifier's server is the authority when we can reach it.
 */

import type { CertState, CityFeatureCollection, CityId, HalalClaim, Place } from '../types';
import { certificationState } from './trust';

const cache = new Map<CityId, Place[]>();
const inflight = new Map<CityId, Promise<Place[]>>();
export const attributions = new Map<CityId, string[]>();

/** Load a city, from memory if we already have it. */
export function loadCity(city: CityId): Promise<Place[]> {
    const hit = cache.get(city);
    if (hit) return Promise.resolve(hit);

    const running = inflight.get(city);
    if (running) return running;

    const p = fetchCity(city)
        .then((places) => {
            cache.set(city, places);
            inflight.delete(city);
            return places;
        })
        .catch((err) => {
            inflight.delete(city);
            throw err;
        });

    inflight.set(city, p);
    return p;
}

async function fetchCity(city: CityId): Promise<Place[]> {
    const res = await fetch(`/data/v3/${city}.geojson`);
    if (!res.ok) throw new Error(`Could not load ${city} (HTTP ${res.status})`);
    const fc = (await res.json()) as CityFeatureCollection;
    attributions.set(city, fc.attribution ?? []);

    return fc.features.map((f) => {
        const p = f.properties;
        const [lng, lat] = f.geometry.coordinates;
        return {
            ...p,
            id: f.id,
            lat,
            lng,
            precision: p.precision,
            certState: certificationState(p.halal),
        } as Place;
    });
}

/** Prefetch a city's data without blocking, for likely next destinations. */
export function prefetchCity(city: CityId): void {
    if (cache.has(city) || inflight.has(city)) return;
    loadCity(city).catch(() => { /* best effort */ });
}

// ---------------------------------------------------------------- live check

export interface LiveCheck {
    city: CityId;
    source: string;
    checkedAt: string;
    updated: number;
    delisted: number;
    unchanged: number;
}

interface LiveEntry {
    validUntil: string | null;
    certificationPending?: boolean;
    validityModel?: 'delisting';
}

const LIVE_SOURCES: Partial<Record<CityId, { id: string; urls: string[]; parse: (payloads: unknown[]) => Map<string, LiveEntry> }>> = {
    hongkong: {
        id: 'halalhk',
        // The directory page sends no CORS header; its WordPress REST
        // representation does, and carries the same table.
        urls: ['https://halalhk.org/wp-json/wp/v2/pages/374?_fields=modified,content'],
        parse: (payloads) => parseHalalHK(payloads[0]),
    },
    paris: {
        id: 'avs',
        urls: [
            'https://equinox.avs.fr/v1/common/partners?type=1',
            'https://equinox.avs.fr/v1/common/partners?type=2',
        ],
        parse: parseAVS,
    },
};

const LIVE_TTL = 6 * 60 * 60 * 1000;

/**
 * Re-check certification for a city. Resolves to null when there is nothing to
 * check, we are offline, or the source cannot be reached — the caller keeps the
 * shipped data in every one of those cases.
 */
export async function refreshCertification(city: CityId, places: Place[]): Promise<LiveCheck | null> {
    const source = LIVE_SOURCES[city];
    if (!source || places.length === 0) return null;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;

    let live: Map<string, LiveEntry>;
    let checkedAt: string;
    try {
        const loaded = await loadLive(source);
        live = loaded.entries;
        checkedAt = loaded.checkedAt;
    } catch {
        return null;
    }
    if (live.size === 0) return null;

    const out: LiveCheck = { city, source: source.id, checkedAt, updated: 0, delisted: 0, unchanged: 0 };

    for (const place of places) {
        if (!place.sources.some((s) => s.id === source.id)) continue;

        const fresh = live.get(place.id);
        if (!fresh) {
            // Gone from the register. For a delisting-based body this IS the
            // revocation signal, and for a dated one it usually means the same.
            place.certState = 'delisted';
            place.liveCheckedAt = checkedAt;
            out.delisted++;
            continue;
        }

        const halal: HalalClaim | undefined = place.halal && {
            ...place.halal,
            validUntil: fresh.validUntil,
            certificationPending: fresh.certificationPending ?? place.halal.certificationPending,
            validityModel: fresh.validityModel ?? place.halal.validityModel,
        };
        const before: CertState = place.certState;
        if (halal) {
            place.halal = halal;
            place.certState = certificationState(halal);
        }
        place.liveCheckedAt = checkedAt;
        if (place.certState === before) out.unchanged++;
        else out.updated++;
    }

    return out;
}

async function loadLive(source: { id: string; urls: string[]; parse: (p: unknown[]) => Map<string, LiveEntry> }) {
    const key = `hy_live_${source.id}`;
    try {
        const raw = localStorage.getItem(key);
        if (raw) {
            const c = JSON.parse(raw) as { at: number; checkedAt: string; entries: [string, LiveEntry][] };
            if (Date.now() - c.at < LIVE_TTL) return { checkedAt: c.checkedAt, entries: new Map(c.entries) };
        }
    } catch { /* corrupt cache — refetch */ }

    const payloads: unknown[] = [];
    for (const url of source.urls) {
        const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'omit', signal: AbortSignal.timeout(15000) });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        payloads.push(await res.json());
    }

    const entries = source.parse(payloads);
    if (entries.size === 0) throw new Error('nothing parseable');
    const checkedAt = new Date().toISOString();
    try {
        localStorage.setItem(key, JSON.stringify({ at: Date.now(), checkedAt, entries: [...entries] }));
    } catch { /* quota — refetch next time */ }
    return { checkedAt, entries };
}

/** Mirrors slugify() in ingest/lib/text.mjs — ids must match exactly. */
function slugify(s: string): string {
    return s.replace(/\s+/g, ' ').trim().toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60);
}

function parseExpiry(raw: string): { date?: string; pending?: boolean } {
    const s = raw.replace(/\s+/g, ' ').trim();
    if (!s) return {};
    if (/^(in process|進行中|processing|renewing)$/i.test(s)) return { pending: true };
    const m = s.match(/^(\d{1,2})[/-]([A-Za-z]{3,9})[/-](\d{2,4})$/);
    if (!m) return {};
    const MONTHS: Record<string, number> = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
    const month = MONTHS[m[2]!.toLowerCase().slice(0, 3)];
    if (!month) return {};
    const day = Number(m[1]);
    let year = Number(m[3]);
    if (year < 100) year += 2000;
    const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const d = new Date(`${iso}T00:00:00Z`);
    if (Number.isNaN(d.getTime()) || d.getUTCDate() !== day) return {};
    return { date: iso };
}

function parseHalalHK(payload: unknown): Map<string, LiveEntry> {
    const out = new Map<string, LiveEntry>();
    const html = (payload as { content?: { rendered?: string } })?.content?.rendered ?? '';
    const table = html.match(/<table[\s\S]*?<\/table>/i)?.[0];
    if (!table) return out;

    const doc = new DOMParser().parseFromString(table, 'text/html');
    const rows: string[][] = [];
    for (const tr of Array.from(doc.querySelectorAll('tr'))) {
        const cells = Array.from(tr.querySelectorAll('td,th')).map((td) => (td.textContent ?? '').replace(/\s+/g, ' ').trim());
        if (cells.length >= 7) rows.push(cells.slice(0, 7));
    }
    const body = /^name$/i.test(rows[0]?.[0] ?? '') ? rows.slice(1) : rows;

    // One English block followed by a Chinese one listing the same venues; only
    // the English block is read, or every id would shift.
    const isCJK = (s: string) => /[一-鿿]/.test(s);
    let split = body.length;
    for (let i = 0; i + 5 <= body.length; i++) {
        if (body.slice(i, i + 5).every((r) => isCJK(r[1] ?? ''))) { split = i; break; }
    }

    const seen = new Map<string, number>();
    for (const cells of body.slice(0, split)) {
        const name = cells[0] ?? '';
        const type = (cells[5] ?? '').toUpperCase();
        if (!name || !['H', 'HK', 'HF'].includes(type)) continue;
        const base = slugify(name) || 'unnamed';
        const n = (seen.get(base) ?? 0) + 1;
        seen.set(base, n);
        const id = `hk:halalhk:${n > 1 ? `${base}-${n}` : base}`;
        if (out.has(id)) continue;
        const e = parseExpiry(cells[6] ?? '');
        out.set(id, { validUntil: e.date ?? null, certificationPending: !!e.pending });
    }
    return out;
}

function parseAVS(payloads: unknown[]): Map<string, LiveEntry> {
    const out = new Map<string, LiveEntry>();
    for (const payload of payloads) {
        const rows = (Array.isArray(payload) ? payload : []) as Array<{ agreementNumber?: string; active?: boolean }>;
        for (const r of rows) {
            if (r?.active === false) continue;
            const agreement = String(r?.agreementNumber ?? '').trim();
            if (!agreement) continue;
            // AVS publishes no expiry — presence in this feed IS the claim.
            out.set(`fr:avs:${agreement.toLowerCase()}`, { validUntil: null, validityModel: 'delisting' });
        }
    }
    return out;
}
